import { useQuery } from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../../query';
import { useStudentApi } from '../../api';
import { studentKeys } from '../../query-key-factory';
import { StudentResponse } from '../../types';
import { ConsumerStudentsSearch, ListConsumerStudentsResponse } from './students.types';

type GetStudentContext = {
  meta?: {
    studentId?: string;
  };
};

const useGetStudent = () => {
  const StudentApi = useStudentApi();

  const getStudent = async (context: GetStudentContext) => {
    const { studentId } = context.meta || {};
    const { data } = await StudentApi.get<StudentResponse>(`/consumer/students/${studentId}`);
    return data;
  };

  return getStudent;
};

type GetStudentsContext = {
  meta?: {
    queryString?: string;
  };
};

/***
 * Gets all students for the current user
 */
const useGetStudents = () => {
  const StudentApi = useStudentApi();

  const getStudents = async (context: GetStudentsContext) => {
    const { queryString } = context.meta || {};
    const { data } = await StudentApi.get<ListConsumerStudentsResponse>(
      `/consumer/students?${queryString}`
    );
    return data;
  };

  return getStudents;
};

/***
 * Gets a single student by id
 */
const useShowConsumerStudent = <T = StudentResponse>({
  studentId,
  select,
}: {
  studentId: string;
  select?: (data: StudentResponse) => T;
}) => {
  const getStudent = useGetStudent();

  return useQuery({
    queryKey: [...studentKeys.id('user').consumer.students.id(studentId).detail],
    queryFn: getStudent,
    meta: { studentId },
    enabled: !!studentId,
    select,
  });
};

interface UseListStudentsArgs<T> {
  enabled?: boolean;
  queryOptions?: ConsumerStudentsSearch;
  pagination?: QueryPagination;
  select?: (data: ListConsumerStudentsResponse) => T;
}
/***
 * Gets all students for the current user
 */
const useListConsumerStudents = <T = ListConsumerStudentsResponse>({
  enabled = true,
  queryOptions,
  pagination,
  select,
}: UseListStudentsArgs<T>) => {
  const getStudents = useGetStudents();
  const { queryString, queryObject, queryPagination } = createQueryOptions<ConsumerStudentsSearch>({
    search: queryOptions,
    pagination,
  });

  return useQuery({
    queryKey: [...studentKeys.id('user').consumer.students.all, queryObject, queryPagination],
    queryFn: getStudents,
    enabled,
    meta: { queryString },
    select,
  });
};

export { useListConsumerStudents, useShowConsumerStudent };
